// Sign in with Apple through Apple's own JS: a popup on this site that hands
// back an ID token, exchanged with Supabase by signInWithIdToken. This route
// needs no client secret, so nothing expires every six months the way the
// redirect flow's signed secret does. The script loads only when the button
// is drawn.
import { makeNonce } from './googleButton';

const APPLE_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

interface AppleIdAuth {
  init(config: {
    clientId: string;
    scope: string;
    redirectURI: string;
    usePopup: boolean;
    nonce?: string;
  }): void;
  signIn(): Promise<{
    authorization?: { id_token?: string };
    user?: { name?: { firstName?: string; lastName?: string } };
  }>;
}

declare global {
  interface Window {
    AppleID?: { auth?: AppleIdAuth };
  }
}

let loading: Promise<AppleIdAuth> | null = null;

function loadAppleId(): Promise<AppleIdAuth> {
  const ready = window.AppleID?.auth;
  if (ready) return Promise.resolve(ready);
  if (loading) return loading;
  loading = new Promise<AppleIdAuth>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = APPLE_SRC;
    script.async = true;
    script.onload = () => {
      const api = window.AppleID?.auth;
      if (api) resolve(api);
      else reject(new Error('Apple sign-in did not load.'));
    };
    script.onerror = () => reject(new Error('Apple sign-in did not load.'));
    document.head.appendChild(script);
  }).catch((error: unknown) => {
    loading = null;
    throw error;
  });
  return loading;
}

export interface AppleSignIn {
  /** Opens Apple's popup. Call straight from the click: a popup opened
   * after an await is blocked. Resolves to the ID token and its raw nonce,
   * or null when the person closes the popup. `name` is the person's name,
   * which Apple sends only the first time they sign in to this site. */
  signIn(): Promise<{ token: string; nonce: string; name: string | null } | null>;
}

let prepared: Promise<AppleSignIn> | null = null;

/** Loads Apple's script and initialises it once per page with one nonce. The
 * return URL is the site's origin, which must be listed on the Services ID
 * (the popup flow never navigates to it, but Apple checks it). */
export function prepareAppleSignIn(clientId: string): Promise<AppleSignIn> {
  if (prepared) return prepared;
  prepared = (async () => {
    const nonce = await makeNonce();
    const api = await loadAppleId();
    api.init({
      clientId,
      scope: 'name email',
      redirectURI: window.location.origin,
      usePopup: true,
      nonce: nonce.hashed,
    });
    return {
      async signIn() {
        try {
          const response = await api.signIn();
          const token = response.authorization?.id_token;
          const parts = [response.user?.name?.firstName, response.user?.name?.lastName];
          const name =
            parts
              .filter((part) => part?.trim())
              .join(' ')
              .trim() || null;
          return token ? { token, nonce: nonce.raw, name } : null;
        } catch {
          // Closing the popup rejects; that is not an error to show.
          return null;
        }
      },
    };
  })().catch((error: unknown) => {
    prepared = null;
    throw error;
  });
  return prepared;
}

export function __resetAppleSignInForTests(): void {
  prepared = null;
  loading = null;
}
